import { FormOfAddress, UploadState } from '@synergy/models';
import { createSelector } from 'reselect';
import { errorsToTouch, getLocalizedString, nullIfEmpty } from '@synergy/utils';

import { AppState } from '../../../../../../store/models/app-state';
import { ContactDetailOverviewFormValues } from '../models/contact-detail-overview-form-values';
import { ContactDetailOverviewRequest } from '../api/models/contact-detail-overview-request';
import { EmbeddedContact } from '../../../../../bonus-settlements/models/embedded-contact';
import { bestLocaleSelector, localesSelector } from '../../../../../../common/selectors/locale-selectors';
import { getCountry } from '../../../../utils/get-country-values';

const contactDetailOverviewState = (state: AppState) => state.contactsAndOrganizations.contacts.contactDetail.overview;

export const contactSelector = createSelector(
    contactDetailOverviewState,
    detailOverviewState => detailOverviewState.contact
);

export const contactDetailOverviewBankAccountsSelector = createSelector(
    contactDetailOverviewState,
    detailOverviewState => ({
        formValues: detailOverviewState.bankAccounts.map(bankAccount => ({
            ...bankAccount,
            media: bankAccount.media.map(m => ({ ...m, state: UploadState.Finished })),
        })),
        errors: detailOverviewState.bankAccountErrors,
    })
);

/**
 * The defaults the mask fills in for values the server left empty, and the
 * country resolved for the reader's locale.
 *
 * Shared with the unsaved-changes baseline (#2159): compared against raw server
 * values, every one of these would read as something the user typed.
 */
const withDefaults = (
    contact: EmbeddedContact | undefined,
    bestLocale: ReturnType<typeof bestLocaleSelector>,
    locales: ReturnType<typeof localesSelector>
): ContactDetailOverviewFormValues => {
    const formValues: ContactDetailOverviewFormValues = {
        id: contact?.id,
        firstName: contact?.firstName ?? '',
        lastName: contact?.lastName ?? '',
        title: contact?.title ?? '',
        mainAccount: contact?.mainAccount,
        mainAccountPosition: getLocalizedString(locales, contact?.mainAccountPosition?.name),
        internalNotes: contact?.internalNotes ?? '',
        image: contact?.image,
        phones: contact?.phones ?? [],
        emails: contact?.emails ?? [],
        bankAccounts: contact?.bankAccounts ?? [],
        addresses: (contact?.addresses ?? []).map(address => {
            return {
                ...address,
                country: getCountry(bestLocale, address.country),
            };
        }),
        website: contact?.website ?? '',
        dateOfBirth: contact?.dateOfBirth,
        formOfAddress: contact?.formOfAddress,
        mainEmail: contact?.mainEmail ?? '',
        mainPhone: contact?.mainPhone ?? '',
        receivesShopOrderMails: contact?.receivesShopOrderMails ?? true,
    };

    return formValues;
};

export const contactDetailOverviewFormDataSelector = createSelector(
    bestLocaleSelector,
    localesSelector,
    contactDetailOverviewState,
    (bestLocale, locales, detailOverviewState) => ({
        formValues: withDefaults(detailOverviewState.contact, bestLocale, locales),
        errors: detailOverviewState.overviewErrors,
        touched: errorsToTouch(detailOverviewState.overviewErrors),
    })
);

/** What the mask compares against to decide whether anything is unsaved. */
export const contactDetailOverviewBaselineSelector = createSelector(
    bestLocaleSelector,
    localesSelector,
    contactDetailOverviewState,
    (bestLocale, locales, detailOverviewState) =>
        detailOverviewState.baseline ? withDefaults(detailOverviewState.baseline, bestLocale, locales) : undefined
);

export const contactDetailOverviewRequestSelector = createSelector(contactDetailOverviewState, detailOverviewState => {
    const contact = detailOverviewState.contact;

    if (!contact) {
        return;
    }

    const phones = contact.phones.map(phone => ({
        ...phone,
        id: phone.id && phone.id < 0 ? undefined : phone.id,
    }));

    const emails = contact.emails.map(email => ({
        ...email,
        id: email.id && email.id < 0 ? undefined : email.id,
    }));

    const addresses = contact.addresses.map(address => ({
        ...address,
        id: address.id && address.id < 0 ? undefined : address.id,
        country: address.country,
        region: address.region?.code,
    }));

    const request: ContactDetailOverviewRequest = {
        firstName: contact.firstName ?? '',
        lastName: contact.lastName ?? '',
        formOfAddress: contact.formOfAddress ?? FormOfAddress.Mr,
        title: nullIfEmpty(contact.title),
        dateOfBirth: contact.dateOfBirth ?? null,
        mainAccountId: contact.mainAccount?.id ?? null,
        internalNotes: nullIfEmpty(contact.internalNotes),
        mainEmail: contact?.mainEmail ?? '',
        emails: emails,
        mainPhone: nullIfEmpty(contact?.mainPhone),
        receivesShopOrderMails: contact?.receivesShopOrderMails ?? true,
        phones: phones,
        website: nullIfEmpty(contact.website),
        addresses,
    };

    return request;
});
